<template>
  <div class="employees" :class="{'is-inactive': isInactive}">
    <button class="btn-delete" type="button" @click="onUserDeleteClick" v-if="newUser" title="Удалить этого сотрудника"
            data-qa="employee-button-delete">
      <vita-icon name="cross-dark"/>
    </button>
    <span class="btn-phone" v-if="isWFMReaderRole && phone" :title="formatPhoneNumber(phone)">
      <vita-icon name="phone"/>
    </span>
    <vita-modal :title="modalTitle" v-if="openModal">
      <vita-button color="red" width="inline" @click="deleteNewUser(zupGuid)"
                   data-qa="employee-button-delete-confirm">Да</vita-button>
      <vita-button color="blue" width="inline" @click="onUserDeleteCancel"
                   data-qa="employee-button-delete-cancel">Нет</vita-button>
    </vita-modal>
    <div class="employees__name">
      <div class="employees__external-icon" title="Сотрудник не из вашей аптеки" v-if="external">
        <vita-icon name="external-user"/>
      </div>
      <div class="employees__external-icon" title="ПСТ" v-if="pstUser && !external">
        <vita-icon class="-icon" name="profi-user"/>
      </div>
      <h4 :title="clearNull(name)">{{ clearNull(name) }}</h4>
    </div>
    <div class="employees__info">
      <div class="employees__position" :title="position">{{ position }}</div>
      <div class="employees__number">({{ number }})</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import VitaIcon from '@/components/UI/VitaIcon.vue';
import VitaButton from '@/components/UI/VitaButton.vue';
import VitaModal from '@/components/UI/VitaModal.vue';
import { checkUnsavedChanges, clearNull, formatPhoneNumber } from '@/utils/main';
import store from '@/store';
import { computed, ref } from 'vue';
import { reachGoal } from '@/utils/metrika';

interface Props {
  name?: string
  position?: string
  number?: number
  newUser: boolean
  zupGuid: string
  external: boolean
  pstUser: boolean
  phone: string | null
  dateEmployment: string | null
}

const props = defineProps<Props>()
const openModal = ref<boolean>(false)
const modalTitle = ref<string>('Вы уверены, что хотите удалить сотрудника?')

const deleteNewUser = (zupGuid: string) => {
  store.commit('data/deleteUser', zupGuid)
  checkUnsavedChanges()
  reachGoal('timetable-employee-delete-confirm')
}

const isWFMReaderRole = computed<boolean>(() => {
  return store.getters['getWFMReaderRole']
})

// Проверка даты трудоустройства сотрудника
const isInactive = computed<boolean>(() => {
  const now = new Date()
  
  // Если дата найма в будущем - сотрудник ещё не трудоустроен
  if (props.dateEmployment && new Date(props.dateEmployment) > now) return true
  return false
})

const onUserDeleteClick = () => {
  openModal.value = true
  reachGoal('timetable-employee-delete-click')
}

const onUserDeleteCancel = () => {
  openModal.value = false
  reachGoal('timetable-employee-delete-cancel')
}
</script>

<style scoped lang="scss">
@use '../static/style/variables.scss' as *;
@use '../static/style/mixins.scss' as *;

.employees {
  height: 94px;
  padding: 0 12px;
  background: $bg-color-light;
  border-radius: 0 3px 3px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 6px;
  border: 1px solid #CCCCCC;
  position: relative;

  &.hover {
    background: #ACDCFF
  }

  h4 {
    font-size: 16px;
    font-weight: 500;
    line-height: 19px;
    margin-bottom: 10px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    text-overflow: ellipsis;
    padding-right: 20px;
    position: relative;
  }

  &__info {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__position {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  &__name {
    position: relative;

    &:has(.employees__external-icon) {
      h4 {
        margin-bottom: 6px;
        z-index: 1;
      }

      h4::before {
        content: '';
        display: inline-block;
        width: 20px;
        height: 20px;
        margin-right: 4px;
      }
    }
  }

  &__external-icon {
    position: absolute;
    top: 4px;
    left: 0;
    z-index: 2;
  }
}

.btn-delete {
  background: transparent;
  border: 0;
  outline: none;
  padding: 0;
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 2;
  cursor: pointer;
}

.btn-phone {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 3;
  min-width: 20px;
  width: 20px;
  height: 20px;
}

.btn-delete + .btn-phone {
  top: 28px;
  right: 8px;
}

.is-inactive > div {
  opacity: .5;
}
</style>
